import React, { useMemo } from 'react'
import { Card, Collapse } from 'antd'
import { AppstoreOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { currency, qtyToCurrency } from 'utils/formatting'
import { ItemRow, ItemsRow } from 'components/apps/Reports/PosShift/Detail/ItemsRow'
import SectionHeader from 'components/apps/Reports/PosShift/Detail/SectionHeader'
import styles from './detail.module.scss'

const { Panel } = Collapse

const ProductsSold = ({ data, productCategories, loading }) => {
  const { formatMessage } = useIntl()

  const { categoriesWithProducts, overallTotalQty, overallTotalAmount, hasItems } = useMemo(() => {
    const items = Array.isArray(data) ? data : []
    const categories = Array.isArray(productCategories) ? productCategories : []

    const uniqueCategories = Array.from(
      new Map(categories.filter((c) => c && c.id != null).map((c) => [c.id, c])).values(),
    )

    const grouped = uniqueCategories
      .map((category) => {
        const products = items.filter((p) => p.pos_product_category_id === category.id)
        return {
          id: category.id,
          name: category.name,
          products,
          totalQty: products.reduce((s, p) => s + (Number(p?.qty) || 0), 0),
          totalAmount: products.reduce((s, p) => s + (Number(p?.amount) || 0), 0),
        }
      })
      .filter((cat) => cat.products.length > 0)

    return {
      categoriesWithProducts: grouped,
      overallTotalQty: items.reduce((s, p) => s + (Number(p?.qty) || 0), 0),
      overallTotalAmount: items.reduce((s, p) => s + (Number(p?.amount) || 0), 0),
      hasItems: items.length > 0,
    }
  }, [data, productCategories])

  if (!loading && !hasItems) {
    return null
  }

  const renderProductList = (products) => (
    <ItemsRow>
      {products.map((product) => (
        <ItemRow key={product.id} title={product.name || '-'} amount={qtyToCurrency(product.qty)} />
      ))}
    </ItemsRow>
  )

  const renderCategoryHeader = (category) => (
    <div className={styles.categoryHeader}>
      <span className={styles.categoryName}>
        {category.name || '-'}
        <span className={styles.categoryCount}>({category.products.length})</span>
      </span>
      <span className={styles.categoryTotal}>{currency(category.totalAmount)}</span>
    </div>
  )

  const expandIcon = ({ isActive }) => (
    <span className={`${styles.expandIcon} ${isActive ? styles.expandIconActive : ''}`}>
      {isActive ? <MinusOutlined /> : <PlusOutlined />}
    </span>
  )

  return (
    <Card
      className={styles.card}
      loading={loading}
      title={
        <SectionHeader
          icon={<AppstoreOutlined />}
          iconClassName={styles.iconTeal}
          title={formatMessage({ id: 'report.products_sold' })}
        />
      }
    >
      {categoriesWithProducts.length > 0 ? (
        <Collapse ghost expandIcon={expandIcon} className={styles.productsCollapse}>
          {categoriesWithProducts.map((category) => (
            <Panel header={renderCategoryHeader(category)} key={category.id}>
              {renderProductList(category.products)}
            </Panel>
          ))}
        </Collapse>
      ) : (
        renderProductList(Array.isArray(data) ? data : [])
      )}

      <div className={styles.overallTotalFooter}>
        <span className={styles.overallTotalLabel}>
          {formatMessage({ id: 'report.overall_total' })}
          <span className={styles.categoryCount}> ({qtyToCurrency(overallTotalQty)})</span>
        </span>
        <span className={styles.overallTotalValue}>{currency(overallTotalAmount)}</span>
      </div>
    </Card>
  )
}

export default ProductsSold
